import { computed, ref, type Ref } from 'vue';

/**
 * Composable для debounce обновлений активного пункта меню.
 * 
 * Предотвращает визуальные скачки при быстрых изменениях активного пункта
 * из внешних источников (например, при скроллинге контента), но сохраняет
 * мгновенный отклик на клики пользователя.
 * 
 * @param internalActiveId - Реактивная ссылка на текущий активный ID
 * @param onActiveChange - Callback для применения изменения активного элемента
 * @param debounceMs - Задержка debounce в миллисекундах (по умолчанию 150ms)
 * 
 * @example
 * ```typescript
 * const { setActiveDebounced, cancel } = useActiveItemDebounce(
 *   internalActiveId,
 *   (id) => {
 *     // Обновить индикатор и scroll-to-active
 *     rafScheduler.schedule('update-indicator', updateIndicator, 'high');
 *     rafScheduler.schedule('scroll-to-active', () => scrollToActiveItem(id), 'normal');
 *   },
 *   150
 * );
 * 
 * // External update - с debounce
 * watch(() => props.modelValue, (newValue) => {
 *   setActiveDebounced(newValue, false);
 * });
 * 
 * // User click - без debounce
 * const handleClick = (item) => {
 *   setActiveDebounced(item.id, true);
 * };
 * 
 * // Cleanup
 * onUnmounted(() => {
 *   cancel();
 * });
 * ```
 */
export function useActiveItemDebounce(
  internalActiveId: Ref<string | number | null>,
  onActiveChange: (id: string | number | null) => void,
  debounceMs: number = 150
) {
  const debounceTimer = ref<ReturnType<typeof setTimeout> | null>(null);
  let pendingId: string | number | null = null;

  /**
   * Установить активный элемент с опциональным debounce.
   * 
   * @param id - ID элемента для установки как активного
   * @param immediate - Если true, изменение применяется немедленно (для user clicks).
   *                    Если false, изменение debounce (для external updates).
   */
  const setActiveDebounced = (
    id: string | number | null,
    immediate: boolean = false
  ): void => {
    // Отменить предыдущий pending update
    if (debounceTimer.value !== null) {
      clearTimeout(debounceTimer.value);
      debounceTimer.value = null;
    }

    pendingId = id;

    // User-initiated изменения применяются немедленно
    if (immediate) {
      applyActiveChange(id);
      pendingId = null;
      return;
    }

    // External изменения debounce
    debounceTimer.value = setTimeout(() => {
      applyActiveChange(pendingId);
      debounceTimer.value = null;
      pendingId = null;
    }, debounceMs);
  };

  /**
   * Применить изменение активного элемента.
   * Обновляет internalActiveId и вызывает callback для визуальных обновлений.
   * 
   * @param id - ID элемента для установки как активного
   */
  const applyActiveChange = (id: string | number | null): void => {
    // Пропустить если значение не изменилось
    if (internalActiveId.value === id) return;
    
    internalActiveId.value = id;
    onActiveChange(id);
  };

  /**
   * Отменить pending debounced update.
   * Используется при unmount компонента для предотвращения memory leaks.
   */
  const cancel = (): void => {
    if (debounceTimer.value !== null) {
      clearTimeout(debounceTimer.value);
      debounceTimer.value = null;
      pendingId = null;
    }
  };

  /**
   * Computed флаг наличия pending debounced update.
   * Полезен для отладки и тестирования.
   */
  const hasPending = computed(() => debounceTimer.value !== null);

  return {
    setActiveDebounced,
    cancel,
    hasPending
  };
}
